import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ArrowDown } from "lucide-react";

/**
 * prompt-kit style chat container: scrollable message area with intelligent
 * auto-stick-to-bottom and a floating scroll-to-bottom button.
 */
const ChatContainer = forwardRef(({ children, className = "" }, ref) => {
  const scrollRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const scrollToBottom = useCallback((behavior = "auto") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    stickToBottomRef.current = true;
  }, []);

  useImperativeHandle(ref, () => ({
    scrollToBottom,
    isNearBottom: () => stickToBottomRef.current,
  }));

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distance < 100;
    setShowScrollButton(distance > 220);
  }, []);

  // Stick to bottom while new content streams in.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof MutationObserver === "undefined") return undefined;
    const observer = new MutationObserver(() => {
      if (stickToBottomRef.current) {
        el.scrollTop = el.scrollHeight;
      }
    });
    observer.observe(el, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="relative flex-1 min-h-0 w-full">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className={`h-full w-full overflow-y-auto scrollbar-hide ${className}`}
      >
        {children}
      </div>
      {showScrollButton && (
        <button
          type="button"
          onClick={() => scrollToBottom("smooth")}
          className="absolute bottom-3 left-1/2 z-10 -translate-x-1/2 rounded-full border border-gray-200 bg-white p-2 text-gray-600 shadow-md transition hover:bg-gray-50"
          aria-label="Scroll to bottom"
        >
          <ArrowDown size={16} />
        </button>
      )}
    </div>
  );
});

export default ChatContainer;
